/**
 * LLM Monitoring Service
 * Client-side service for interacting with LLM monitoring API
 */

import api from './api';

export interface LLMRequest {
  id: number;
  user_id: string;
  session_id?: string;
  timestamp: string;
  model: string;
  fallback_used: boolean;
  provider_used?: string;
  request_type: 'routing' | 'tool_call' | 'general';
  request_messages: any[];
  response_content: any;
  tool_name?: string;
  tool_parameters?: any;
  reasoning?: string;
  confidence?: number;
  finish_reason?: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost: number;
  latency_ms: number;
  cache_hit: boolean;
  success: boolean;
  error_message?: string;
}

export interface LLMRequestFilters {
  userId?: string;
  startDate?: string;
  endDate?: string;
  model?: string;
  requestType?: string;
  success?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: 'timestamp' | 'cost' | 'latency_ms' | 'total_tokens';
  orderDir?: 'ASC' | 'DESC';
}

export interface LLMStats {
  period: string;
  total_requests: number;
  unique_users?: number;
  successful_requests: number;
  failed_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost: number;
  avg_latency: number;
  max_latency: number;
  min_latency: number;
  cache_hits: number;
  cache_hit_rate: number;
}

export interface ModelStats {
  [model: string]: {
    calls: number;
    totalTokens: number;
    promptTokens: number;
    completionTokens: number;
    totalCost: number;
    avgLatency: string;
    successRate: string;
    avgCostPerCall: string;
    totalLatency?: number;
    errors?: number;
  };
}

export interface UsageLimits {
  withinLimits: boolean;
  warnings: string[];
  limits: {
    dailyCost: { used: number; limit: number };
    monthlyCost: { used: number; limit: number };
    dailyCalls: { used: number; limit: number };
    monthlyCalls: { used: number; limit: number };
  };
}

class LLMMonitoringService {
  /**
   * Get LLM requests with filters
   */
  async getRequests(filters: LLMRequestFilters = {}): Promise<{
    success: boolean;
    data?: LLMRequest[];
    error?: string;
    count?: number;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/llm/requests?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching LLM requests:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Get specific LLM request details
   */
  async getRequestById(id: number): Promise<{
    success: boolean;
    data?: LLMRequest;
    error?: string;
  }> {
    try {
      const response = await api.get(`/llm/requests/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching LLM request:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Get aggregated statistics
   */
  async getStats(options: {
    userId?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'hour' | 'day' | 'month';
    includeCacheStats?: boolean;
    period?: 'daily' | 'monthly';
  } = {}): Promise<{
    success: boolean;
    data?: {
      aggregated: LLMStats[];
      cache?: any;
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/llm/stats?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching LLM stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Get model-specific performance metrics
   */
  async getModelStats(): Promise<{
    success: boolean;
    data?: ModelStats;
    error?: string;
  }> {
    try {
      const response = await api.get('/llm/stats/models');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching model stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Get user-specific metrics
   */
  async getUserStats(userId?: string, period: 'daily' | 'monthly' = 'daily'): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (userId) params.append('userId', userId);
      params.append('period', period);

      const response = await api.get(`/llm/stats/users?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Check usage limits
   */
  async checkUsageLimits(options: {
    userId?: string;
    dailyCostLimit?: number;
    monthlyCostLimit?: number;
    dailyCallLimit?: number;
    monthlyCallLimit?: number;
  } = {}): Promise<{
    success: boolean;
    data?: UsageLimits;
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/llm/limits?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      console.error('Error checking usage limits:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Export LLM requests data
   */
  async exportData(filters: LLMRequestFilters & { format?: 'json' | 'csv' } = {}): Promise<void> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/llm/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `llm-requests-${Date.now()}.${filters.format || 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error exporting LLM data:', error);
      throw error;
    }
  }

  /**
   * Connect to real-time monitoring via SSE
   */
  connectRealtime(onMessage: (data: any) => void, onError?: (error: any) => void): EventSource {
    const token = localStorage.getItem('token');
    const eventSource = new EventSource(`${api.defaults.baseURL}/llm/realtime?token=${token}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      if (onError) onError(error);
    };

    return eventSource;
  }

  /**
   * Cleanup old metrics (admin only)
   */
  async cleanupOldMetrics(daysToKeep: number = 30): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await api.post('/llm/cleanup', { daysToKeep });
      return response.data;
    } catch (error: any) {
      console.error('Error cleaning up metrics:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }
}

export default new LLMMonitoringService();